import { useState } from 'react';
import { Modal, Button, Stack, Text, TextInput, Alert } from '@mantine/core';
import { IconAlertCircle, IconDeviceMobile } from '@tabler/icons-react';
import MFASetupModal from '@components/mfa-setup-modal/MFASetupModal';
import { useTheme } from '@contexts/ThemeContext';
import { loginGlassModal } from '@components/login-background/loginGlassModal';
import '@components/login-background/login-form-glass.css';

interface MFAAddDeviceModalProps {
  opened: boolean;
  onClose: () => void;
  onDeviceAdded: () => void;
  sessionToken: string;
}

export const MFAAddDeviceModal = ({ opened, onClose, onDeviceAdded, sessionToken }: MFAAddDeviceModalProps) => {
  const { mode, colors: themeColors } = useTheme();
  const glassModal = loginGlassModal(mode);
  const [deviceName, setDeviceName] = useState('');
  const [createdByName, setCreatedByName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState<{
    qrCodeUrl: string;
    secret: string;
    backupCodes: string[];
    deviceId: number;
  } | null>(null);

  const handleAddDevice = async () => {
    if (!deviceName.trim() || !createdByName.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Call API to add device
      // For now, show a notification
      console.log('[ADD DEVICE] Adding device:', { deviceName, createdByName, hasSessionToken: Boolean(sessionToken) });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock response - in real implementation, call the API
      const mockResponse = {
        deviceId: 456,
        qrCodeUrl:
          'otpauth://totp/Jarvis:user@example.com?algorithm=SHA1&digits=6&issuer=Jarvis&period=30&secret=JBSWY3DPEHPK3PXP',
        secret: 'JBSWY3DPEHPK3PXP',
        backupCodes: ['12345678', '87654321', '11223344', '55667788', '99001122'],
      };

      setSetupData(mockResponse);
      setShowSetup(true);
    } catch (error) {
      console.error('[ADD DEVICE] Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyDevice = async (code: string) => {
    try {
      console.log('[VERIFY DEVICE] Verifying code:', code);
      // TODO: Call verify-setup API

      // Simulate success
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setShowSetup(false);
      onDeviceAdded();
      handleClose();
    } catch (error) {
      console.error('[VERIFY DEVICE] Error:', error);
      throw error;
    }
  };

  const handleClose = () => {
    setDeviceName('');
    setCreatedByName('');
    setShowSetup(false);
    setSetupData(null);
    onClose();
  };

  if (showSetup && setupData) {
    return (
      <MFASetupModal
        opened={showSetup}
        qrCodeUrl={setupData.qrCodeUrl}
        secret={setupData.secret}
        backupCodes={setupData.backupCodes}
        deviceId={setupData.deviceId}
        onVerify={handleVerifyDevice}
        onClose={() => setShowSetup(false)}
        isVerifying={false}
      />
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size="md"
      title={
        <Stack gap="xs">
          <Text size="xl" fw={700} style={{ color: glassModal.text }}>
            Tambahkan Device Baru
          </Text>
          <Text size="sm" style={{ color: glassModal.textMuted }}>
            Add a new device for team members to access this account
          </Text>
        </Stack>
      }
      zIndex={10002}
      overlayProps={glassModal.overlayProps}
      styles={glassModal.styles}
    >
      {/* Opens over the login page, so it wears the login's glass. */}
      <div className="login-glass" data-login-surface={glassModal.surface}>
        <Stack gap="lg">
          <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
            <Text size="sm">
              After adding the device, the team member will scan the QR code with their authenticator app
            </Text>
          </Alert>

          <TextInput
            label="Device Name"
            placeholder="e.g., Friend's iPhone"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            required
            leftSection={<IconDeviceMobile size={16} />}
            styles={{
              input: {
                backgroundColor: themeColors.formFields,
                borderColor: themeColors.border,
                color: themeColors.text,
              },
              label: {
                color: themeColors.text,
              },
            }}
          />

          <TextInput
            label="User Name"
            placeholder="e.g., Jane Smith"
            value={createdByName}
            onChange={(e) => setCreatedByName(e.target.value)}
            required
            styles={{
              input: {
                backgroundColor: themeColors.formFields,
                borderColor: themeColors.border,
                color: themeColors.text,
              },
              label: {
                color: themeColors.text,
              },
            }}
          />

          <Stack gap="xs" mt="md">
            <Button
              fullWidth
              onClick={handleAddDevice}
              disabled={!deviceName.trim() || !createdByName.trim()}
              loading={isLoading}
            >
              Generate QR Code
            </Button>
            <Button fullWidth variant="subtle" color="gray" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </div>
    </Modal>
  );
};

export default MFAAddDeviceModal;
